/**
 * Bhaat search overlay manager.
 *
 * DOM hooks:
 *   [data-search-trigger]   -> opens overlay
 *   [data-search-close]     -> closes overlay
 *   [data-search-overlay]   -> overlay container (hidden by default)
 *   [data-search-input]     -> the <input type="search"> inside the overlay
 *   [data-search-form]      -> the form (submit -> /search?q=)
 *   [data-predictive-results] -> where predictive results are injected
 *   [data-search-helper]    -> default empty/popular state
 *
 * Events dispatched on `window`:
 *   'search:open'
 *   'search:close'
 *
 * Listens on `window`:
 *   'search:open' / 'search:close'
 */

(function () {
  const DEBOUNCE_MS = 300;
  const SUGGEST_LIMIT = 6;

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function $$(sel, root) {
    return Array.from((root || document).querySelectorAll(sel));
  }

  function getOverlay() {
    return document.querySelector('[data-search-overlay]');
  }

  /* Hydrogen SearchOverlay: popular searches = first 5 header collections, else the fallback terms (server-rendered). */
  let popularSynced = false;
  function syncPopularFromHeader(overlay) {
    if (popularSynced) return;
    popularSynced = true;
    const pills = overlay.querySelector('[data-popular-pills]');
    const links = $$('.header-menu-collections a.header-menu-item-dynamic').slice(0, 5);
    if (!pills || !links.length) return;
    pills.replaceChildren(...links.map((a) => {
      const pill = document.createElement('a');
      pill.href = a.getAttribute('href');
      pill.className = 'search-overlay-pill';
      pill.setAttribute('data-search-close', '');
      pill.textContent = a.textContent.trim();
      return pill;
    }));
  }

  function openOverlay() {
    const overlay = getOverlay();
    if (!overlay) return;
    syncPopularFromHeader(overlay);
    overlay.removeAttribute('hidden');
    overlay.setAttribute('data-visible', '');
    document.documentElement.classList.add('search-open');
    requestAnimationFrame(() => {
      const input = overlay.querySelector('[data-search-input]');
      if (input) input.focus();
    });
  }

  function closeOverlay() {
    const overlay = getOverlay();
    if (!overlay) return;
    overlay.setAttribute('hidden', '');
    overlay.removeAttribute('data-visible');
    document.documentElement.classList.remove('search-open');
    const input = overlay.querySelector('[data-search-input]');
    if (input) input.value = '';
    resetResults();
  }

  function resetResults() {
    const helper = $('[data-search-helper]');
    if (helper) helper.removeAttribute('hidden');
    const results = $('[data-predictive-results]');
    if (!results) return;
    /* clear injected sections but keep the helper */
    $$('[data-predictive-section]', results).forEach((el) => el.remove());
    const viewAll = $('[data-predictive-view-all]', results);
    if (viewAll) viewAll.remove();
  }

  /* ------- predictive fetch ------- */

  let lastTerm = '';
  let abortCtrl = null;

  async function fetchPredictive(term) {
    if (abortCtrl) abortCtrl.abort();
    abortCtrl = new AbortController();
    const params = new URLSearchParams();
    params.set('q', term);
    params.set('resources[type]', 'product,collection,page,query');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[limit]', String(SUGGEST_LIMIT));
    params.set('resources[limit_scope]', 'each');
    try {
      const res = await fetch(`/search/suggest.json?${params.toString()}`, {
        headers: { Accept: 'application/json' },
        signal: abortCtrl.signal,
      });
      const data = await res.json();
      renderResults(data?.resources?.results || {}, term);
    } catch (e) {
      if (e.name !== 'AbortError') {
        /* swallow */
      }
    }
  }

  function escapeHTML(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(amount) {
    if (amount == null) return '';
    const n = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (Number.isNaN(n)) return '';
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "INR" }).format(n);
  }

  function renderResults(items, term) {
    const container = $('[data-predictive-results]');
    if (!container) return;

    /* clear previous injected sections */
    $$('[data-predictive-section]', container).forEach((el) => el.remove());
    const viewAllOld = $('[data-predictive-view-all]', container);
    if (viewAllOld) viewAllOld.remove();

    const products = items.products || [];
    const collections = items.collections || [];
    const pages = items.pages || [];
    const queries = items.queries || [];

    const total = products.length + collections.length + pages.length + queries.length;

    const helper = $('[data-search-helper]', container);
    if (term.length < 2) {
      if (helper) helper.removeAttribute('hidden');
      return;
    }
    if (helper) helper.setAttribute('hidden', '');

    if (total === 0) {
      const empty = document.createElement('div');
      empty.setAttribute('data-predictive-section', '');
      empty.innerHTML = `<p>No results found for <q>${escapeHTML(term)}</q></p>`;
      container.appendChild(empty);
      return;
    }

    /* queries datalist */
    if (queries.length) {
      const dl = document.createElement('div');
      dl.setAttribute('data-predictive-section', '');
      dl.className = 'predictive-search-result';
      dl.innerHTML =
        `<h5>Suggestions</h5><ul>` +
        queries
          .map(
            (q) =>
              `<li class="predictive-search-result-item"><a href="/search?q=${encodeURIComponent(
                q.text,
              )}"><div><span>${q.styled_text || escapeHTML(q.text)}</span></div></a></li>`,
          )
          .join('') +
        `</ul>`;
      container.appendChild(dl);
    }

    if (products.length) {
      const sec = document.createElement('div');
      sec.setAttribute('data-predictive-section', '');
      sec.className = 'predictive-search-result';
      sec.innerHTML =
        `<h5>Products</h5><ul>` +
        products
          .map((p) => {
            const img = p.image ? `<img src="${p.image}" alt="${escapeHTML(p.title)}" width="50" height="50">` : '';
            const price = p.price ? `<small>${formatMoney(p.price)}</small>` : '';
            return `<li class="predictive-search-result-item"><a href="${p.url}">${img}<div><p>${escapeHTML(
              p.title,
            )}</p>${price}</div></a></li>`;
          })
          .join('') +
        `</ul>`;
      container.appendChild(sec);
    }

    if (collections.length) {
      const sec = document.createElement('div');
      sec.setAttribute('data-predictive-section', '');
      sec.className = 'predictive-search-result';
      sec.innerHTML =
        `<h5>Collections</h5><ul>` +
        collections
          .map((c) => {
            const img = c.image ? `<img src="${c.image}" alt="${escapeHTML(c.title)}" width="50" height="50">` : '';
            return `<li class="predictive-search-result-item"><a href="${c.url}">${img}<div><span>${escapeHTML(
              c.title,
            )}</span></div></a></li>`;
          })
          .join('') +
        `</ul>`;
      container.appendChild(sec);
    }

    if (pages.length) {
      const sec = document.createElement('div');
      sec.setAttribute('data-predictive-section', '');
      sec.className = 'predictive-search-result';
      sec.innerHTML =
        `<h5>Pages</h5><ul>` +
        pages
          .map(
            (p) =>
              `<li class="predictive-search-result-item"><a href="${p.url}"><div><span>${escapeHTML(
                p.title,
              )}</span></div></a></li>`,
          )
          .join('') +
        `</ul>`;
      container.appendChild(sec);
    }

    /* view-all link */
    const viewAll = document.createElement('a');
    viewAll.setAttribute('data-predictive-view-all', '');
    viewAll.className = 'search-overlay-view-all';
    viewAll.href = `/search?q=${encodeURIComponent(term)}`;
    viewAll.innerHTML = `View all results for <q>${escapeHTML(term)}</q> →`;
    container.appendChild(viewAll);
  }

  /* ------- debounced input handler ------- */

  let debounceTimer = null;
  function onInput(e) {
    const term = (e.target.value || '').trim();
    lastTerm = term;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      if (term.length < 2) {
        resetResults();
        return;
      }
      fetchPredictive(term);
    }, DEBOUNCE_MS);
  }

  /* ------- wire delegated listeners ------- */

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-search-trigger]');
    if (trigger) {
      e.preventDefault();
      openOverlay();
      return;
    }

    const close = e.target.closest('[data-search-close]');
    if (close) {
      const overlay = close.closest('[data-search-overlay]') || close.closest('.search-overlay');
      if (overlay || close.matches('.search-overlay-backdrop')) {
        /* Links (popular-search pills) close the overlay AND navigate, like Hydrogen's onClick={close}. */
        if (!close.matches('a[href]')) e.preventDefault();
        closeOverlay();
      }
    }
  });

  document.addEventListener('input', (e) => {
    const input = e.target.closest('[data-search-input]');
    if (input) onInput(e);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      const overlay = getOverlay();
      if (overlay && !overlay.hasAttribute('hidden')) {
        closeOverlay();
      }
    }
    /* Cmd/Ctrl+K opens overlay */
    if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      openOverlay();
    }
  });

  window.addEventListener('search:open', openOverlay);
  window.addEventListener('search:close', closeOverlay);

  window.BhaatSearch = {
    open: openOverlay,
    close: closeOverlay,
  };
})();
